import { useCallback, useRef } from 'react';

interface UseTooltipHandlersProps {
    disabled: boolean;
    trigger: 'hover' | 'click';
    enterDelay: number;
    leaveDelay: number;
    onOpenChange: (open: boolean) => void;
}

export const useTooltipHandlers = ({
    disabled,
    trigger,
    enterDelay,
    leaveDelay,
    onOpenChange,
}: UseTooltipHandlersProps) => {
    const timeoutRef = useRef<NodeJS.Timeout>();

    const clearCurrentTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (disabled || trigger !== 'hover') return;

        clearCurrentTimeout();

        timeoutRef.current = setTimeout(() => {
            onOpenChange(true);
        }, enterDelay);
    }, [disabled, trigger, enterDelay, onOpenChange, clearCurrentTimeout]);

    const handleMouseLeave = useCallback(() => {
        if (disabled || trigger !== 'hover') return;

        clearCurrentTimeout();

        timeoutRef.current = setTimeout(() => {
            onOpenChange(false);
        }, leaveDelay);
    }, [disabled, trigger, leaveDelay, onOpenChange, clearCurrentTimeout]);

    const handleClick = useCallback(() => {
        if (disabled || trigger !== 'click') return;
        onOpenChange(true);
    }, [disabled, trigger, onOpenChange]);

    const handleClickAway = useCallback(() => {
        if (trigger === 'click') {
            onOpenChange(false);
        }
    }, [trigger, onOpenChange]);

    return {
        handleMouseEnter,
        handleMouseLeave,
        handleClick,
        handleClickAway,
        clearTimeout: clearCurrentTimeout,
    };
};
